import useTranslation from 'next-translate/useTranslation';
import { useEffect, useRef, useState } from 'react';
import { PlayBase, Theatre } from 'typings/api';
import { determinePluralFromNumber, Permalink, truncateTitle } from '../../helpers';
import { useOnScrollElement, usePlayRow } from '../../hooks';
import AppLink from '../AppLink/AppLink';
import DramoxImage from '../DramoxImage';
import styles from './index.module.scss';
import Analytics from 'helpers/Analytics';
import { Namespace } from '../../enums';

interface Props {
  theatre: Theatre;
}

interface RowPlayProps {
  plays: Partial<PlayBase>[];
  theatre: Theatre;
  playsCount: number;
  loadImages: boolean;
}

const WITH_NUMBER = '_with-number';

const RowPlays = ({ plays, theatre, playsCount, loadImages }: RowPlayProps) => {
  const ref = useRef(null);
  const { lang } = useTranslation();
  const [slicePlays, setSlicePlays] = useState<Partial<PlayBase>[]>();

  usePlayRow(ref, loadImages);

  useEffect(() => {
    let sliced = plays;
    if (playsCount > 4) {
      sliced = plays.slice(0, 4);
      sliced[3].title = WITH_NUMBER;
    }
    setSlicePlays(sliced);
  }, [plays, setSlicePlays]);

  const getTileLink = (play: Partial<PlayBase>) => {
    if (play.title === WITH_NUMBER) {
      return Permalink.theatre(theatre);
    }
    return Permalink.play(play as PlayBase, lang);
  };

  const getAnchorChild = (play: Partial<PlayBase>) => {
    if (play.title === WITH_NUMBER) {
      return <div>+{theatre.plays_count - slicePlays?.length + 1}</div>;
    }

    return (
      <>
        <DramoxImage
          loadImmediately={loadImages}
          width={170}
          height={96}
          usePlaceholder={true}
          src={play.poster_url}
          alt={play.title}
        />
        <h3>{truncateTitle(play.title)}</h3>
      </>
    );
  };

  return (
    <div className="theatre-wrapper">
      <div className="category-section theatre-section">
        <div ref={ref} className="scroll-wrapper-theatre">
          <ul className="play-list swiper-wrapper">
            {slicePlays?.map((play) => (
              <li
                key={play.id}
                className={[play?.title === WITH_NUMBER ? 'with-number' : '', 'swiper-slide'].join(' ')}
              >
                <AppLink href={getTileLink(play)}>
                  <a>{getAnchorChild(play)}</a>
                </AppLink>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const TheartreSection = ({ theatre }: Props): JSX.Element => {
  const ref = useRef(null);
  const [elementTop, setElementTop] = useState<number>(null);

  const { t } = useTranslation(Namespace.Theaters);

  useEffect(() => {
    if (ref?.current) {
      // calculate top only once on initial render
      setElementTop(ref?.current?.getBoundingClientRect().top);
    }
  }, [ref]);

  const loadImages = useOnScrollElement(elementTop);

  return (
    <li ref={ref} key={theatre.id} className={[styles.theatreRow, styles.withBackground].join(' ')}>
      <div className={styles.info}>
        <AppLink href={Permalink.theatre(theatre)}>
          <a>
            {theatre.poster_url ? (
              <DramoxImage
                width={48}
                placeholderClass={styles.imgPlaceholder}
                height={48}
                alt={theatre?.name}
                loadImmediately={loadImages}
                usePlaceholder={true}
                src={theatre?.poster_url}
              />
            ) : (
              <div className={styles.imgPlaceholder}>{theatre?.name?.substr(0, 1)}</div>
            )}
          </a>
        </AppLink>

        <div className={styles.text}>
          <h3>
            <AppLink href={Permalink.theatre(theatre)}>
              <a>{theatre?.name}</a>
            </AppLink>
          </h3>
          <p>{t(`plays_count_${determinePluralFromNumber(theatre?.plays_count)}`, { count: theatre?.plays_count })}</p>
        </div>
      </div>
      <RowPlays
        playsCount={theatre?.plays_count as unknown as number}
        theatre={theatre}
        plays={theatre.plays.map((p) => Object.assign({}, p, { theatre: theatre })) as Partial<PlayBase>[]}
        loadImages={loadImages}
      />
    </li>
  );
};

export default TheartreSection;
